import React from 'react';

/**
 * The dashboards' motion primitives, in one place so every card and chart reveals on the same rule.
 *
 * Motion here only ever marks data arriving or a value changing: it plays once, stays under a second, never
 * loops, and is skipped entirely under `prefers-reduced-motion`.
 */

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Reports, once, when the element behind `ref` has scrolled into view.
 *
 * "In view" is half of the element showing - or half of the scroll area, for an element taller than that -
 * rather than the element passing a fixed line in the viewport: the last card on a page may never scroll
 * far enough up to cross one, and then it never reveals at all. The scroll area is `<main>`, which is what
 * scrolls in the app shell, clipped to the window.
 *
 * `ref` is a callback ref, so an element that only mounts once its data lands - after a skeleton - is
 * picked up when it appears.
 */
export const useRevealOnScroll = <T extends HTMLElement = HTMLDivElement>() => {
  const [node, setNode] = React.useState<T | null>(null);
  const [revealed, setRevealed] = React.useState(prefersReducedMotion);

  React.useEffect(() => {
    if (!node || revealed) return;

    const check = () => {
      const rect = node.getBoundingClientRect();
      const area = node.closest('main')?.getBoundingClientRect();
      const top = Math.max(0, area?.top ?? 0);
      const bottom = Math.min(window.innerHeight, area?.bottom ?? window.innerHeight);
      const visible = Math.min(rect.bottom, bottom) - Math.max(rect.top, top);

      if (
        bottom > top &&
        rect.height > 0 &&
        visible >= Math.min(rect.height, bottom - top) * 0.5 &&
        rect.right > 0 &&
        rect.left < window.innerWidth
      ) {
        setRevealed(true);
      }
    };

    // Capture phase, so `<main>`'s own scroll is heard - it does not bubble. The observer is only a nudge
    // for layout changes that move the element without a scroll; the check itself decides.
    document.addEventListener('scroll', check, true);
    window.addEventListener('resize', check);
    const observer =
      'IntersectionObserver' in window ? new IntersectionObserver(check, { threshold: [0, 0.25, 0.5, 0.75, 1] }) : null;
    observer?.observe(node);

    // Two frames: the element has to paint once in its hidden state, or a CSS transition has no "before".
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(check);
    });

    return () => {
      document.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
      observer?.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [node, revealed]);

  return { ref: setNode, revealed };
};

interface CountUpOptions {
  /** Hold at the start value until true - typically `revealed`. */
  active?: boolean;
  delayMs?: number;
  durationMs?: number;
}

/**
 * Counts the displayed number from 0 up to `target` once `active`, then from wherever it is to each new
 * target - so a refresh that changes a figure glides to it rather than restarting from zero. Intermediate
 * values are rounded; the last frame is `target` exactly.
 */
export const useCountUp = (target: number, { active = true, delayMs = 0, durationMs = 750 }: CountUpOptions = {}) => {
  const reduceMotion = React.useMemo(prefersReducedMotion, []);
  const [shown, setShown] = React.useState(reduceMotion ? target : 0);
  const shownRef = React.useRef(shown);

  React.useEffect(() => {
    if (!active) return;

    const from = shownRef.current;
    if (reduceMotion || from === target) {
      shownRef.current = target;
      setShown(target);
      return;
    }

    let frame = 0;
    const timer = window.setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - start) / durationMs, 1);
        const eased = 1 - (1 - progress) ** 3;
        const next = progress === 1 ? target : Math.round(from + (target - from) * eased);
        shownRef.current = next;
        setShown(next);
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [active, target, delayMs, durationMs, reduceMotion]);

  return shown;
};

/**
 * True for a moment after `value` changes - never for the first value, so only a refresh that actually
 * moved a figure lights it up.
 */
export const useChangeFlash = (value: unknown, durationMs = 1200) => {
  const previous = React.useRef(value);
  const [flashing, setFlashing] = React.useState(false);

  React.useEffect(() => {
    if (Object.is(previous.current, value)) return;
    previous.current = value;

    setFlashing(true);
    const timer = window.setTimeout(() => setFlashing(false), durationMs);
    return () => window.clearTimeout(timer);
  }, [value, durationMs]);

  return flashing;
};

/** How long a chart's bars take to grow in on their first draw - recharts' `animationDuration`. */
export const CHART_GROW_MS = 600;

/**
 * True until a chart has been drawn for `durationMs`: lets its first draw animate and nothing after. A
 * period switch that changes the number of bars would otherwise morph one set into the other, and replaying
 * the grow-in on every reload stops meaning "the data just arrived".
 */
export const useFirstDrawAnimation = (drawn: boolean, durationMs = CHART_GROW_MS + 300) => {
  const [done, setDone] = React.useState(prefersReducedMotion);

  React.useEffect(() => {
    if (!drawn || done) return;
    const timer = window.setTimeout(() => setDone(true), durationMs);
    return () => window.clearTimeout(timer);
  }, [drawn, done, durationMs]);

  return !done;
};
